import { Loader2, AlertCircle } from 'lucide-react';
import { Text, Button } from '@musa.tech/design-system';
import { useInvoiceCollectCalculation } from '@/hooks/useInvoiceCollectCalculation';
import type {
  CollectCalculation,
  CollectRuleRationale,
} from '@/integrations/faturamento/client';

const formatBRL = (value: number | null | undefined): string =>
  value == null
    ? '—'
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

export function CalculationBody({ data }: { data: CollectCalculation }) {
  return (
    <div className="flex flex-col gap-3">
      {data.rules.map((rule, index) => (
        <RuleBlock key={index} rule={rule} showBreakdown={data.show_breakdown} />
      ))}

      <div className="flex items-center justify-between border-t border-neutral-200 pt-2">
        <Text variant="label" size="small" className="text-neutral-900">
          Total
        </Text>
        <Text variant="label" size="small" className="text-neutral-900">
          {formatBRL(data.collect_total)}
        </Text>
      </div>

      {!data.show_breakdown && (
        <Text variant="caption" size="xsmall" className="text-neutral-500">
          Detalhamento não disponível para esta coleta.
        </Text>
      )}
    </div>
  );
}

function RuleBlock({
  rule,
  showBreakdown,
}: {
  rule: CollectRuleRationale;
  showBreakdown: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Text variant="label" size="small" className="text-neutral-900">
        {rule.label}
      </Text>
      <Text variant="caption" size="xsmall" className="text-neutral-500">
        {rule.summary}
      </Text>

      {showBreakdown &&
        rule.terms.map((term, index) => (
          <div key={index} className="flex items-start justify-between gap-3 pt-1">
            <Text variant="body" size="small" className="text-neutral-600">
              {term.label}
              {term.detail ? ` · ${term.detail}` : ''}
            </Text>
            <Text variant="body" size="small" className="whitespace-nowrap text-neutral-700">
              {formatBRL(term.value)}
            </Text>
          </div>
        ))}
    </div>
  );
}

function CompactCalculationBody({ data }: { data: CollectCalculation }) {
  return (
    <div className="flex flex-col gap-1">
      {data.rules.map((rule, index) => (
        <div key={index} className="flex flex-wrap items-baseline gap-x-2">
          <Text variant="caption" size="xsmall" className="text-neutral-700">
            {rule.label}:
          </Text>
          <Text variant="caption" size="xsmall" className="text-neutral-900">
            {formatBRL(rule.subtotal)}
          </Text>
          {data.show_breakdown && rule.terms.length > 0 && (
            <Text variant="caption" size="xsmall" className="text-neutral-500">
              ({rule.terms.map(term => term.detail || term.label).filter(Boolean).join(' · ')})
            </Text>
          )}
        </div>
      ))}

      {data.rules.length > 1 && (
        <div className="flex items-baseline gap-x-2 border-t border-neutral-200 pt-1">
          <Text variant="caption" size="xsmall" className="text-neutral-700">
            Total:
          </Text>
          <Text variant="caption" size="xsmall" className="text-neutral-900">
            {formatBRL(data.collect_total)}
          </Text>
        </div>
      )}
    </div>
  );
}

interface CollectCalculationDetailsProps {
  cycleId: string;
  collectId: string;
  variant?: 'default' | 'compact';
}

/**
 * Busca e renderiza o cálculo. Só é montado quando a linha aninhada está aberta, então o
 * `enabled` da query é sempre true aqui — a montagem é o próprio gatilho de busca.
 */
export function CollectCalculationDetails({
  cycleId,
  collectId,
  variant = 'default',
}: CollectCalculationDetailsProps) {
  const { data, isLoading, isError, refetch } = useInvoiceCollectCalculation({
    cycleId,
    collectId,
    enabled: true,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
        <Text variant="body" size="small" className="text-neutral-600">
          Calculando…
        </Text>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-error-500" />
          <Text variant="body" size="small" className="text-neutral-700">
            Não foi possível carregar o cálculo.
          </Text>
        </div>
        <Button variant="secondary" size="sm" onClick={() => refetch()}>
          Tentar de novo
        </Button>
      </div>
    );
  }

  if (!data || !data.available || data.rules.length === 0) {
    return (
      <Text variant="caption" size="xsmall" className="text-neutral-600">
        Cálculo não disponível para este item.
      </Text>
    );
  }

  return variant === 'compact'
    ? <CompactCalculationBody data={data} />
    : <CalculationBody data={data} />;
}
